// frontend/src/farm/herdPlayback.mjs — playing the server's herd intentions (spec 4d §3), pure.
// Every viewer computes the same position from the same intention and the server clock.
const dist = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1]);

export function clockOffset(serverNowMs, clientNowMs) { return serverNowMs - clientNowMs; }

export function positionAt(intent, nowMs) {
  if (!intent || intent.kind === 'inside') return { x: 0, y: 0, moving: false, faceLeft: false, visible: false, pose: 'idle' };
  if (intent.kind === 'idle') {
    return { x: intent.at[0], y: intent.at[1], moving: false, faceLeft: intent.face === -1, visible: true, pose: intent.pose ?? 'idle',
      since: nowMs - intent.t0 };
  }
  const path = intent.path;
  let left = Math.max(0, (intent.speed * (nowMs - intent.t0)) / 1000);
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i], b = path[i + 1], seg = dist(a, b);
    const faceLeft = (b[0] - a[0]) - (b[1] - a[1]) < 0;
    if (left <= seg && seg > 0) {
      const f = left / seg;
      return { x: a[0] + (b[0] - a[0]) * f, y: a[1] + (b[1] - a[1]) * f, moving: true, faceLeft, visible: true, pose: 'walk' };
    }
    left -= seg;
  }
  const last = path[path.length - 1], prev = path[Math.max(0, path.length - 2)];
  return { x: last[0], y: last[1], moving: false, faceLeft: (last[0] - prev[0]) - (last[1] - prev[1]) < 0, visible: true, pose: 'stand' };   // arrived: head up until the server's rest comes
}

export function shiftIntent(intent, d) {
  if (intent.kind === 'walk') return { ...intent, path: intent.path.map(([x, y]) => [x, y + d]) };
  if (intent.kind === 'idle') return { ...intent, at: [intent.at[0], intent.at[1] + d] };
  return intent;
}

export function fallbackIntent(animal, home) {
  const at = animal.pos ?? [home.x + 0.5, home.y + 0.5];
  return { id: animal.id, kind: 'idle', at: [at[0], at[1]], pose: 'idle', t0: 0, until: 0 };
}

// Intentions carry the farm size the server planned them for; a farm that grew since (every y moved
// down by the growth) draws them shifted — correct whether the grow or the new intentions came first.
export function intentForSize(intent, size) {
  const d = intent?.size ? size - intent.size : 0;
  return d ? shiftIntent(intent, d) : intent;
}

// Which still frame an idle animal shows: head up (standing, or a cow looking at you) or eating.
// One that stops to eat stands SETTLE_MS first (owner: stop-and-lean in one frame looked jerky).
export const SETTLE_MS = 600;
export function restFrame(pose, sinceMs = Infinity) {
  if (pose === 'stand' || pose === 'look') return 'stand';
  return sinceMs < SETTLE_MS ? 'stand' : 'idle';
}
